<script setup lang="ts">
import { renderToString } from "@vue/server-renderer";
import type { Position } from "geojson";
import type { LatLngExpression, LatLngLiteral, LatLngTuple } from "leaflet";
import type { ComputedRef } from "vue";
import { computed, createSSRApp, onMounted } from "vue";
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  DEFAULT_ZOOM,
} from "~/modules/cartography/constants";
import type { CartographySettings } from "~/modules/cartography/models/CartographySettings.interface";
import type { FeatureCollectionWithStyle } from "~/modules/cartography/models/FeatureCollectionWithStyle.interface";
import type { MapCenter } from "~/modules/cartography/models/MapCenter.interface";
import L from "~/modules/cartography/utils/leafletWithMarkerClusterImports";
import ILxcTarget from "~icons/lxc/target";

const props = defineProps<{
  cartographySettings: CartographySettings;
  points?: FeatureCollectionWithStyle;
  center?: MapCenter;
  zoom?: number;
}>();

const mapContainer = ref<HTMLElement | null>(null);
let mapInstance: L.Map | null = null;

const defaultCenter: ComputedRef<LatLngExpression> = computed(
  () =>
    [
      props.center?.latitude ?? DEFAULT_LATITUDE,
      props.center?.longitude ?? DEFAULT_LONGITUDE,
    ] as LatLngTuple,
);

const defaultZoom: ComputedRef<number> = computed(
  () => props.zoom ?? DEFAULT_ZOOM,
);

const formatLatLng = (coordinates: Position): LatLngLiteral => {
  if (coordinates.length >= 2) {
    return { lat: coordinates[0], lng: coordinates[1] };
  }
  return { lat: DEFAULT_LATITUDE, lng: DEFAULT_LONGITUDE };
};

/**
 * Use server-side rendering to use the raw HTML in the map (required by leaflet `iconDiv`). The icon is wrapped in a
 * div to allow style modifications (for instance the icon color).
 *
 * @param component
 * @param componentClass
 * @param color
 * @param overlayComponent
 */
const renderComponentAsHtmlWithStyle = async (
  component: object,
  componentClass?: string[] | string,
  color?: string,
  overlayComponent?: object | string,
) => {
  const element = createSSRApp(component);
  const componentAsHtml = await renderToString(element);
  // overlay component refers to the device type icon. This icon is displayed on top of the main marker icon.
  if (overlayComponent) {
    if (typeof overlayComponent === "string") {
      return `<div style="color: ${color}" class="${componentClass} icon-class">
        ${componentAsHtml}
        <div class="inner-icon-class">${overlayComponent}</div>
      </div>`;
    }
    const overlayElement = createSSRApp(overlayComponent);
    const overlayComponentAsHtml = await renderToString(overlayElement);
    return `<div ${color ? `style="color: ${color}"` : ""} class="${componentClass ?? ""} icon-class">
      ${componentAsHtml}
      <div class="inner-icon-class">${overlayComponentAsHtml}</div>
    </div>`;
  }
  return `<div ${color ? `style="color: ${color}"` : ""} class="${componentClass}">${componentAsHtml}</div>`;
};

const createMarker = async (
  coordinates: Position,
  icon?: object | undefined,
  iconClass?: string[] | string | undefined,
  color?: string | undefined,
  iconAnchor?: L.PointExpression | undefined,
  overlayIcon?: object | string | undefined,
  onClick?: () => void | undefined,
) => {
  const iconAsHtml = await renderComponentAsHtmlWithStyle(
    icon ?? ILxcTarget,
    iconClass,
    color,
    overlayIcon,
  );
  const marker = L.marker(formatLatLng(coordinates), {
    icon: L.divIcon({
      html: iconAsHtml,
      className: "map-icon",
      iconAnchor,
    }),
  });
  if (onClick !== undefined) {
    marker.on("click", () => {
      onClick();
    });
  }
  return marker;
};

async function init() {
  if (!mapContainer.value) {
    return;
  }

  // Clean up previous map instance if any (prevents the "Map container is already initialized" issue)
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }

  const baseMaps: Record<string, L.TileLayer> = {};
  let baseMapFirstKey: string = "";

  for (const provider of props.cartographySettings.providers) {
    baseMapFirstKey = baseMapFirstKey || provider.name;
    baseMaps[provider.name] = L.tileLayer(provider.url, provider.options);
  }

  mapInstance = L.map(mapContainer.value, {
    center: defaultCenter.value,
    zoom: defaultZoom.value,
    layers: [baseMaps[baseMapFirstKey]],
  });

  if (Object.keys(baseMaps).length > 1) {
    L.control.layers(baseMaps).addTo(mapInstance);
  }

  if (props.points?.features && props.points.features.length > 0) {
    const markers = L.markerClusterGroup({ chunkedLoading: true });

    for (const feature of props.points.features) {
      if (feature?.geometry?.coordinates !== undefined) {
        const marker = await createMarker(
          feature.geometry.coordinates,
          feature?.properties.icon,
          feature?.properties.class,
          feature?.properties.color,
          feature?.properties.iconAnchor,
          feature?.properties.overlayIcon,
          feature?.properties.onClick,
        );
        markers.addLayer(marker);
      }
    }

    if (markers.getLayers().length > 0) {
      mapInstance.addLayer(markers);
      mapInstance.fitBounds(markers.getBounds(), { animate: false });
    }

    // Force Leaflet to recalculate size in case the container was not visible at init
    setTimeout(() => {
      mapInstance?.invalidateSize();
    }, 100);
  }
}

watch(props.cartographySettings, init, { deep: true });
watch(() => props.points, init, { deep: true });

onMounted(init);
onUnmounted(() => {
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }
});
</script>

<template>
  <!-- The div containing the map initialized in the onMounted method -->
  <div ref="mapContainer" class="!z-0 w-full h-full" />
</template>

<style lang="scss">
.map-icon {
  background: transparent; /* Remove any background - leaflet adds an ugly white background by default */
}
/* Override of classes from leaflet components */
.leaflet-control-layers-base,
.leaflet-control-layers-overlays {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  label {
    span {
      display: flex;
      column-gap: 0.2rem;
      justify-content: start;
      align-items: center;
      .leaflet-control-layers-selector {
        margin-top: 0;
        position: static;
        &:disabled {
          opacity: 50%;
          background: #667085;
        }
      }
    }
  }
}
a[href] {
  &.leaflet-control-zoom-in,
  &.leaflet-control-zoom-out {
    text-decoration: none;
  }
}
.leaflet-marker-icon {
  .icon-class {
    position: relative;
    .inner-icon-class {
      color: white;
      position: absolute;
      top: 15%;
      left: 35%;
      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
  &.marker-cluster {
    background-color: rgba(78, 114, 163, 0.6);
    div {
      background-color: rgba(14, 73, 129, 1);
      span {
        color: white;
      }
    }
  }
}
</style>
